import 'flag-icons/css/flag-icons.min.css';
import 'primeflex/primeflex.css';

import { useContext } from 'react';
import GuideText from 'components/v2/GuideText';
import { toaster } from 'components/v2/toast';
import { ProcessStatus } from 'constants/constants';
import { themeOptions } from 'constants/constants';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CompanyService from 'services/CompanyService';
import { AdminLicenseeModel, UpdateLicenseeModel, VerifierType } from 'services/EpdClient';
import AdminLicenseesService from 'services/admin/AdminLicenseesService';
import AdminService from 'services/admin/AdminService';
import AdminUserService from 'services/admin/AdminUserService';
import { H1NewUI } from 'styles/v2/Styles.styled';
import { ButtonSmall } from 'styles/Styles.styled';
import { Option } from 'types/types';
import ManageLicenseeDialog from './ManageLicenseeDialog';
import EmptyLicenseeList from './EmptyLicenseeList';
import LicenseeList from './LicenseeList';
import { uploadLicenseeLogo } from 'services/api/epdApi';
import { CompanyContext } from 'contexts/CompanyContextProvider';

const ManageLicensees = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [licensees, setLicensees] = useState<AdminLicenseeModel[]>([]);
  const [isLicenseeDialogVisible, setIsLicenseeDialogVisible] = useState<boolean>(false);
  const [selectedLicensee, setSelectedLicensee] = useState<AdminLicenseeModel | null>(null);
  const [countriesDs, setCountriesDs] = useState<Option[]>([]);
  const [individualVerifiersDs, setIndividualVerifiersDs] = useState<Option[]>([]);
  const [certificationBodyVerifiersDs, setCertificationBodyVerifiersDs] = useState<Option[]>([]);
  const [usersDs, setUsersDs] = useState<Option[]>([]);
  const { companyId, licensee, refreshLicensee } = useContext(CompanyContext);

  const fetchAllCountries = async () => {
    const countries = await AdminService.getCountries(undefined);
    setCountriesDs(
      countries.map((country) => {
        return { value: country.id, label: country.name } as Option;
      })
    );
  };

  const fetchAllUsers = async () => {
    const result = await AdminUserService.getUsers();
    setUsersDs(
      result
        .filter((u) => u.firstName != null)
        .map((user) => {
          return { value: user.id, label: `${user.firstName} ${user.lastName}` } as Option;
        })
    );
  };

  const fetchAllVerifiers = async () => {
    const result = await CompanyService.getCompanyVerifiers(undefined, undefined);
    setIndividualVerifiersDs(
      result
        .filter((x) => x.verifierType == VerifierType.Individual)
        .map((v, index) => {
          return { value: v.id, label: v.name } as Option;
        })
    );

    setCertificationBodyVerifiersDs(
      result
        .filter((x) => x.verifierType == VerifierType.Body)
        .map((v, index) => {
          return { value: v.id, label: v.name } as Option;
        })
    );
  };

  const fetchLicensees = async () => {
    setStatus(ProcessStatus.Fetching);
    try {
      const result = await AdminLicenseesService.getLicensees();
      setLicensees(result);
      setStatus(ProcessStatus.Success);
    } catch (error) {
      console.error(error);
      setStatus(ProcessStatus.Error);
    }
  };

  const handleEditLicensee = (licensee: AdminLicenseeModel) => {
    setSelectedLicensee(licensee);
    setIsLicenseeDialogVisible(true);
  };

  const createLicensee = async (values: any) => {
    let model = {
      name: values.name,
      countries: values.countries.map((i: any) => i.value),
      users: values.users.map((i: any) => i.value),
      theme: values.theme,
      supportEmail: values.supportEmail,
      localHomepage: values.localHomepage,
      hasVerifiersList: values.hasVerifiersList,
      licenseeIndividualVerifiers: values.licenseeIndividualVerifiers.map((i: any) => i.value),
      licenseeCertificationBodyVerifiers: values.licenseeCertificationBodyVerifiers.map((i: any) => i.value),
      logotypeUrl: null,
    };
    setStatus(ProcessStatus.Fetching);
    try {
      const result = await AdminLicenseesService.createLicensee(model);
      if (values.licenseeLogo instanceof File && result?.id) {
        await uploadLicenseeLogo(result.id, values.licenseeLogo);
      }
      setStatus(ProcessStatus.Success);
      setIsLicenseeDialogVisible(false);
      toaster({ severity: 'success', summary: 'Success', details: 'Licensee has been created' });
      fetchLicensees();
    } catch (error) {
      toaster({ severity: 'error', summary: 'Error', details: 'Licensee creation error' });
      console.error(error);
      setStatus(ProcessStatus.Error);
    }
  };

  const updateLicensee = async (values: any) => {
    setStatus(ProcessStatus.Fetching);
    try {
      const model: UpdateLicenseeModel = {
        id: selectedLicensee?.id || '',
        name: values.name,
        countries: values.countries.map((i: any) => i.value),
        users: values.users.map((i: any) => i.value),
        theme: values.theme,
        supportEmail: values.supportEmail,
        localHomepage: values.localHomepage,
        hasVerifiersList: values.hasVerifiersList,
        licenseeIndividualVerifiers: values.licenseeIndividualVerifiers.map((i: any) => i.value),
        licenseeCertificationBodyVerifiers: values.licenseeCertificationBodyVerifiers.map((i: any) => i.value),
        removeLogo: values.licenseeLogo === null,
        logotypeUrl: selectedLicensee?.licenseeLogo || null,
      };
      await AdminLicenseesService.updateLicensee(selectedLicensee?.id || '', model);

      if (values.licenseeLogo instanceof File && selectedLicensee?.id) {
        await uploadLicenseeLogo(selectedLicensee?.id, values.licenseeLogo);
      }

      setStatus(ProcessStatus.Success);
      setIsLicenseeDialogVisible(false);
      toaster({ severity: 'success', summary: 'Success', details: 'Licensee has been updated' });
      fetchLicensees();

      if (licensee?.id === selectedLicensee?.id) {
        await refreshLicensee(companyId);
      }
    } catch (error) {
      toaster({ severity: 'error', summary: 'Error', details: 'Licensee update error' });
      console.error(error);
      setStatus(ProcessStatus.Error);
    }
  };

  useEffect(() => {
    fetchLicensees();
    fetchAllCountries();
    fetchAllUsers();
    fetchAllVerifiers();
  }, []);

  if (status === ProcessStatus.Fetching) {
    return <ProgressSpinner />;
  }

  return (
    <>
      <ConfirmDialog />
      <div className="flex justify-content-between w-full py-2">
        <H1NewUI>{t('manageLicensees.licensees')}</H1NewUI>
        <ButtonSmall
          style={{ paddingLeft: '1.4rem', paddingRight: '1.4rem', alignSelf: 'center' }}
          onClick={() => {
            setSelectedLicensee(null);
            setIsLicenseeDialogVisible(true);
          }}
        >
          {t('manageLicensees.createLicensee')}
        </ButtonSmall>
        <ManageLicenseeDialog
          show={isLicenseeDialogVisible}
          onHide={() => setIsLicenseeDialogVisible(false)}
          item={selectedLicensee!}
          onCreate={createLicensee}
          onEdit={updateLicensee}
          countriesDs={countriesDs}
          usersDs={usersDs}
          themesDs={themeOptions}
          verifiersDs={individualVerifiersDs}
          certificationBodyDs={certificationBodyVerifiersDs}
        />
      </div>

      <GuideText value={'Here you can manage the organisations which are related to the regional Licensees.'} />

      {licensees.length > 0 ? (
        <LicenseeList licensees={licensees} fetchLicensees={fetchLicensees} handleEditLicensee={handleEditLicensee} />
      ) : (
        <EmptyLicenseeList />
      )}

      <div className="card" style={{ width: '100%' }}></div>
    </>
  );
};

export default ManageLicensees;
